import type { ShiftFormManagerDashboard, ShiftFormManagerDashboardFilters, ShiftFormProductionComparison } from "../../../shared/shift-production-form/contracts";
import { analysisDepartments, analysisDepartmentLines, columnAxis, type ColumnMeasure, type AnalysisDepartment } from "./analysis-columns";
import { analysisShiftTimes } from "./analysis-presentation";
import { shiftLabels } from "../shift-production-form/line-options";

type LineName = NonNullable<ShiftFormManagerDashboardFilters["lineName"]>;
type Shift = NonNullable<ShiftFormManagerDashboardFilters["shift"]>;
type Metrics = ShiftFormProductionComparison["machines"][number]["shifts"][number];
export type AnalysisChartDestination =
  | { kind: "DEPARTMENT"; departmentName: AnalysisDepartment }
  | { kind: "MACHINE"; lineName: LineName }
  | { kind: "SHIFT"; lineName: LineName; shift: Shift };
export type DepartmentRow = {
  id: string;
  label: string;
  color: string;
  formCount: number;
  value: number | null;
  state: "RECORDED" | "MISSING" | "EMPTY";
  missingDurationCount?: number;
  destination: AnalysisChartDestination | null;
};
const shifts = ["00_08", "08_16", "16_00"] as const;
const round = (value: number) => Math.round(value * 100) / 100;

export function analysisLineDepartment(dashboard: ShiftFormManagerDashboard, lineName: LineName): AnalysisDepartment | undefined {
  return dashboard.analytics.productionComparison?.machines.find(machine => machine.lineName === lineName)?.departmentName
    ?? analysisDepartments.find(department => analysisDepartmentLines(dashboard, department.name).includes(lineName))?.name;
}

function summarize(metrics: readonly Metrics[], measure: ColumnMeasure): Pick<DepartmentRow, "formCount" | "value" | "state"> {
  const formCount = metrics.reduce((sum, item) => sum + item.formCount, 0);
  if (!formCount) return { formCount, value: null, state: "EMPTY" };
  let total = 0;
  for (const item of metrics) {
    if (!item.formCount) continue;
    if (measure.kind === "DOWNTIME") {
      if (item.downtimeMinutes === null) return { formCount, value: null, state: "MISSING" };
      total += item.downtimeMinutes;
    } else {
      const quantity = measure.kind === "OUTPUT" ? item.actualOutputs.find(value => value.unit === measure.unit)
        : item.wasteEntries.find(value => value.key === measure.key && value.unit === measure.unit);
      if (!quantity || quantity.reportingFormCount !== item.formCount) return { formCount, value: null, state: "MISSING" };
      total += quantity.value;
    }
  }
  return { formCount, value: round(total), state: "RECORDED" };
}

/** Uses the complete authorized comparison, never the bounded source rows. */
export function buildAnalysisDepartments(dashboard: ShiftFormManagerDashboard, filters: ShiftFormManagerDashboardFilters, measure: ColumnMeasure, terminal = false) {
  const machines = dashboard.analytics.productionComparison?.machines ?? [];
  const rows: DepartmentRow[] = [];
  const level = filters.lineName ? "shifts" : filters.departmentName ? "lines" : "departments";
  const inScope = (shift: Shift) => !filters.shift || shift === filters.shift;
  const machineMetrics = (lineName: LineName, departmentName?: AnalysisDepartment) => machines.find(machine => machine.lineName === lineName && (!departmentName || machine.departmentName === departmentName))?.shifts.filter(item => inScope(item.shift)) ?? [];
  const durationCount = (lines: LineName[]) => {
    const comparison = dashboard.analytics.downtimeComparison;
    if (measure.kind !== "DOWNTIME" || !comparison) return undefined;
    const entries = comparison.machines.filter(machine => lines.includes(machine.lineName)).flatMap(machine => machine.shifts.filter(item => inScope(item.shift)));
    const formCount = lines.flatMap(line => machineMetrics(line)).reduce((sum, item) => sum + item.formCount, 0);
    return entries.reduce((sum, item) => sum + item.formCount, 0) === formCount
      ? entries.reduce((sum, item) => sum + item.missingDurationFormCount, 0) : undefined;
  };
  const append = (id: string, label: string, color: string, metrics: Metrics[], destination: AnalysisChartDestination | null, missingDurationCount?: number) => {
    const data = summarize(metrics, measure);
    rows.push({ id, label, color, ...data, missingDurationCount, destination: terminal || data.state === "EMPTY" ? null : destination });
  };
  const selectedDepartment = filters.lineName ? analysisLineDepartment(dashboard, filters.lineName) : filters.departmentName;
  for (const department of analysisDepartments) {
    if (selectedDepartment && department.name !== selectedDepartment) continue;
    const lines = analysisDepartmentLines(dashboard, department.name);
    if (!lines.length) continue;
    if (filters.lineName) {
      if (!lines.includes(filters.lineName)) continue;
      for (const [index, shift] of shifts.entries()) {
        if (!inScope(shift)) continue;
        const metrics = machineMetrics(filters.lineName, department.name).filter(item => item.shift === shift);
        const missing = dashboard.analytics.downtimeComparison?.machines.find(machine => machine.lineName === filters.lineName)?.shifts.find(item => item.shift === shift);
        append(shift, `${shiftLabels[shift]} · ${analysisShiftTimes[shift]}`, department.tones[index] ?? department.color, metrics,
          { kind: "SHIFT", lineName: filters.lineName, shift }, measure.kind === "DOWNTIME" && missing?.formCount === metrics.reduce((sum, item) => sum + item.formCount, 0) ? missing?.missingDurationFormCount : undefined);
      }
    } else if (filters.departmentName) {
      for (const lineName of lines) append(lineName, lineName, department.color, machineMetrics(lineName, department.name), { kind: "MACHINE", lineName }, durationCount([lineName]));
    } else {
      append(department.name, department.label, department.color, lines.flatMap(line => machineMetrics(line, department.name)), { kind: "DEPARTMENT", departmentName: department.name }, durationCount(lines));
    }
  }
  const representedLines = analysisDepartments.flatMap(department => analysisDepartmentLines(dashboard, department.name));
  const unclassified = !filters.departmentName && !filters.lineName && machines.some(machine => !machine.departmentName || !representedLines.includes(machine.lineName));
  const missing = rows.some(row => row.state === "MISSING");
  const recorded = rows.filter(row => row.state === "RECORDED");
  const inconsistent = !filters.departmentName && !filters.lineName && !filters.shift && rows.reduce((sum, row) => sum + row.formCount, 0) !== dashboard.summary.submittedFormCount;
  return { rows, level, axis: columnAxis(Math.max(0, ...recorded.map(row => row.value ?? 0))), missing,
    total: missing || unclassified || inconsistent || !recorded.length ? null : round(recorded.reduce((sum, row) => sum + (row.value ?? 0), 0)),
    empty: !rows.some(row => row.state !== "EMPTY"), available: Boolean(dashboard.analytics.productionComparison), unclassified: unclassified || inconsistent,
    unit: measure.kind === "DOWNTIME" ? "dk" : measure.unit };
}
